import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePortal } from '../contexts/PortalContext';
import { IconShield, IconEye, IconEyeOff } from '../components/ui/Icons';
import LoadingScreen from '../components/ui/LoadingScreen';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { isPartnerPortal } = usePortal();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.message || 'Login failed');
    }

    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-page__form-side">
          <LoadingScreen message="Checking session..." />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-page__panel">
        <div className="login-page__panel-glow" />
        <div className="login-page__panel-glow login-page__panel-glow--2" />
        <div className="login-page__panel-content">
          <h2>
            {isPartnerPortal
              ? 'Your reseller workspace for customers, brand, and billing'
              : 'Manage your multi-tenant platform with confidence'}
          </h2>
          <p>
            {isPartnerPortal
              ? 'Create customer workspaces, apply your brand, and choose who invoices each account.'
              : 'Secure control center for products, tenants, integrations, and platform configuration.'}
          </p>
          <div className="login-page__features">
            <div className="login-page__feature">
              <span className="login-page__feature-dot" />
              {isPartnerPortal ? 'Customer workspace provisioning' : 'Product & tenant lifecycle management'}
            </div>
            <div className="login-page__feature">
              <span className="login-page__feature-dot" />
              {isPartnerPortal ? 'White-label login and app chrome' : 'OAuth & email configuration'}
            </div>
            <div className="login-page__feature">
              <span className="login-page__feature-dot" />
              {isPartnerPortal ? 'Wholesale and retail billing assignment' : 'Enterprise-grade security'}
            </div>
          </div>
        </div>
      </div>

      <div className="login-page__form-side">
        <div className="login-card">
          <div className="login-card__brand">
            <div className="login-card__logo">
              <IconShield width={24} height={24} />
            </div>
            <span className="login-card__brand-text">{isPartnerPortal ? 'Reseller portal' : 'PennaiAppz'}</span>
          </div>

          <div className="login-card__header">
            <h1>Welcome back</h1>
            <p>{isPartnerPortal ? 'Sign in to your partner account' : 'Sign in to your super admin account'}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert--error">{error}</div>}

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email address</label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter email address"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="form-input password-input__field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter password"
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-input__toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <IconEyeOff width={18} height={18} /> : <IconEye width={18} height={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
